import React, { useCallback, useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { FileSpreadsheet, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { TableSkeleton } from "~/components/ui/skeletons";
import { TablePagination } from "~/components/ui/table-pagination";
import { toast } from "~/hooks/use-toast";
import { usePagination } from "~/hooks/usePagination";
import { useProducts } from "~/hooks/useProducts";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { supabase } from "~/integrations/supabase/client";
import { useCreditUtilization } from "~/pages/clients/components/client-detail/hooks/useCreditUtilization";
import EmptySalesRequests from "~/pages/clients/components/client-detail/sales-requests/EmptySalesRequests";
import { exportSalesRequestsToExcel } from "~/pages/clients/components/client-detail/sales-requests/exportSalesRequests";
import SalesRequestsFilter from "~/pages/clients/components/client-detail/sales-requests/SalesRequestsFilter";
import SalesRequestsTable from "~/pages/clients/components/client-detail/sales-requests/SalesRequestsTable";
import { useSalesRequests } from "~/pages/clients/components/client-detail/sales-requests/useSalesRequests";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { fetchProfilesUtil } from "~/pages/purchase-orders/hooks/utils/profileUtils";
import { SalesRequest, SalesRequestItem } from "~/types/client";

interface SalesRequestsTabProps {
  clientId: string;
  salesRequestId?: string;
}

const PAGE_SIZE = 10;

// Define a type for the client data
interface ClientData {
  sales_condition_id?: string;
  risk_checked?: boolean;
  name?: string;
}

const SalesRequestsTab: React.FC<SalesRequestsTabProps> = ({
  clientId,
  salesRequestId,
}) => {
  const {
    salesRequests,
    filteredSalesRequests,
    loading,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    selectedSalesRequest,
    setSelectedSalesRequest,
    refetchSalesRequests,
  } = useSalesRequests(clientId);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    filteredSalesRequests,
    PAGE_SIZE
  );

  // Back to the first page whenever the filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, setPage]);

  const { products } = useProducts();
  const { getConditionById } = useSalesConditions();
  const [isExporting, setIsExporting] = useState(false);
  const [orderItems, setOrderItems] = useState<SalesRequestItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [sellerName, setSellerName] = useState<string>("");
  const [sellerInfo, setSellerInfo] = useState<{
    name: string;
    lastname: string;
    email: string;
  }>({
    name: "",
    lastname: "",
    email: "",
  });
  const [approverInfo, setApproverInfo] = useState<{
    name: string;
    lastname: string;
    email: string;
    role: string;
  }>({
    name: "Sistema",
    lastname: "",
    email: "",
    role: "system",
  });
  const [clientRiskChecked, setClientRiskChecked] = useState(false);
  const { fetchCreditUtilization } = useCreditUtilization(clientId);
  const [clientSalesConditionId, setClientSalesConditionId] = useState<
    string | null
  >(null);
  const [clientName, setClientName] = useState<string>("Cliente");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch client sales condition - extracted to a reusable function
  const fetchClientSalesCondition = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("sales_condition_id, risk_checked, name")
        .eq("id", clientId)
        .is("deleted_at", null) // Exclude soft-deleted clients
        .single();

      if (error) throw error;

      // Cast the data to the ClientData type to access the properties
      const clientData = data as ClientData;
      setClientSalesConditionId(clientData?.sales_condition_id || null);
      setClientRiskChecked(clientData?.risk_checked || false);
      setClientName(clientData?.name || "Cliente");
    } catch (error) {
      console.error("Error fetching client data:", error);
    }
  }, [clientId]);

  useEffect(() => {
    const fetchSalesRequestItems = async () => {
      if (!selectedSalesRequest) return;

      setLoadingItems(true);
      try {
        const { data, error } = await supabase
          .from("sales_request_items")
          .select("*")
          .eq("sales_request_id", selectedSalesRequest.id);

        if (error) throw error;

        const items: SalesRequestItem[] = data.map((item) => {
          const matchingProduct = products.find(
            (p) => p.id === item.product_id
          );
          return {
            id: item.id,
            salesRequestId: selectedSalesRequest.id,
            productId: item.product_id,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            discountPercent: item.discount_percent ?? undefined,
          };
        });

        setOrderItems(items);

        if (selectedSalesRequest.createdBy) {
          const sellerProfiles = await fetchProfilesUtil([
            selectedSalesRequest.createdBy,
          ]);
          const seller = sellerProfiles[selectedSalesRequest.createdBy];
          if (seller) {
            setSellerName(
              `${seller.name || ""} ${seller.lastname || ""}`.trim()
            );
            setSellerInfo({
              name: seller.name || "",
              lastname: seller.lastname || "",
              email: seller.email || "",
            });
          } else {
            setSellerName("Vendedor desconocido");
          }
        }

        if (selectedSalesRequest.approvedRejectedBy) {
          const approverProfiles = await fetchProfilesUtil([
            selectedSalesRequest.approvedRejectedBy,
          ]);
          const approver =
            approverProfiles[selectedSalesRequest.approvedRejectedBy];
          if (approver) {
            setApproverInfo({
              name: approver.name || "",
              lastname: approver.lastname || "",
              email: approver.email || "",
              role: approver.role || "unknown",
            });
          }
        }

        // Refresh client risk status
        await fetchClientSalesCondition();
      } catch (error) {
        console.error("Error fetching sales request items:", error);
        setOrderItems([]);
        setSellerName("Error al cargar vendedor");
      } finally {
        setLoadingItems(false);
      }
    };

    fetchSalesRequestItems();
  }, [selectedSalesRequest, products, fetchClientSalesCondition]);

  // Initial fetch of client sales condition
  useEffect(() => {
    fetchClientSalesCondition();
  }, [fetchClientSalesCondition]);

  // Check URL parameters on component mount to auto-open order detail
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderIdToOpen = urlParams.get("open");
    const guiaGenerated = urlParams.get("guia_generated");

    if (
      orderIdToOpen &&
      guiaGenerated === "true" &&
      salesRequests.length > 0 &&
      !loading
    ) {
      // Find the order in the full sales requests array (not just filtered)
      const orderToOpen = salesRequests.find(
        (order) => order.id === orderIdToOpen
      );
      if (orderToOpen) {
        setSelectedSalesRequest(orderToOpen);
      }

      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("open");
      newUrl.searchParams.delete("guia_generated");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [salesRequests, loading]);

  // Listen for client-updated events
  useEffect(() => {
    const handleClientUpdated = (event: CustomEvent<{ clientId: string }>) => {
      // Only refresh if this is the client we're currently viewing
      if (event.detail.clientId === clientId) {
        fetchClientSalesCondition();
        fetchCreditUtilization();
      }
    };

    // Add event listener with type assertion
    window.addEventListener(
      "client-updated",
      handleClientUpdated as EventListener
    );

    // Clean up
    return () => {
      window.removeEventListener(
        "client-updated",
        handleClientUpdated as EventListener
      );
    };
  }, [clientId, fetchClientSalesCondition, fetchCreditUtilization]);

  // Automatically open the order dialog if salesRequestId is provided
  useEffect(() => {
    if (salesRequestId && !selectedSalesRequest && !loading) {
      const found = filteredSalesRequests.find(
        (req) => req.id === salesRequestId
      );
      if (found) {
        setSelectedSalesRequest(found);
        // Remove the sales_request param from the URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("sales_request");
        navigate({ search: newParams.toString() }, { replace: true });
      }
    }
  }, [
    salesRequestId,
    filteredSalesRequests,
    selectedSalesRequest,
    setSelectedSalesRequest,
    loading,
    searchParams,
    navigate,
  ]);

  const getSellerInfo = (sellerId: string) => {
    return sellerInfo;
  };

  const getApproverRejecterInfo = (approverId?: string) => {
    return approverInfo;
  };

  const handleSalesRequestRowClick = (salesRequest: SalesRequest) => {
    setSelectedSalesRequest(salesRequest);
  };

  const handleRefetchRequests = async () => {
    await refetchSalesRequests();
    fetchCreditUtilization();
  };

  const handleExportExcel = async () => {
    if (filteredSalesRequests.length === 0) return;

    setIsExporting(true);
    try {
      const { filename, count, detailCount } = await exportSalesRequestsToExcel({
        salesRequests: filteredSalesRequests,
        clientName,
        getConditionById,
        clientSalesConditionId: clientSalesConditionId || undefined,
      });
      toast({
        title: "Excel exportado",
        description: `Se descargó ${filename} con ${count} notas de venta y ${detailCount} líneas de producto.`,
      });
    } catch (error) {
      console.error("Error exporting sales requests:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo Excel.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const isFilterActive = searchQuery !== "" || statusFilter !== "all";

  return (
    <div className="space-y-6 p-4 pt-6 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SalesRequestsFilter
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        <Button
          variant="outline"
          className="h-9 w-full sm:w-auto"
          onClick={handleExportExcel}
          disabled={isExporting || filteredSalesRequests.length === 0}
        >
          {isExporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="mr-2 h-4 w-4" />
          )}
          Exportar Excel
        </Button>
      </div>

      {loading ? (
        <>
          <TableSkeleton rows={8} columns={8} className="hidden md:block" />
          <div className="space-y-3 md:hidden">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-32 w-full rounded-lg" />
            ))}
          </div>
        </>
      ) : filteredSalesRequests.length === 0 ? (
        <EmptySalesRequests searchActive={isFilterActive} />
      ) : (
        <>
          <SalesRequestsTable
            salesRequests={pageItems}
            onRowClick={handleSalesRequestRowClick}
            clientSalesConditionId={clientSalesConditionId || undefined}
          />
          <TablePagination
            page={page}
            pageCount={pageCount}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            itemLabel="notas de venta"
          />
        </>
      )}

      {selectedSalesRequest && (
        <OrderDetailDialog
          selectedOrder={selectedSalesRequest}
          setSelectedOrder={() => setSelectedSalesRequest(null)}
          getSellerInfo={getSellerInfo}
          getApproverRejecterInfo={getApproverRejecterInfo}
          clientNames={{ [selectedSalesRequest.clientId]: clientName }}
          items={orderItems}
          onOrderStatusChange={handleRefetchRequests}
          clientRiskStatus={{
            [selectedSalesRequest.clientId]: clientRiskChecked,
          }}
        />
      )}
    </div>
  );
};

export default SalesRequestsTab;
